<template>
  <v-responsive>
    <v-row>
      <v-col cols="12">
        <v-subheader class="mb-1 ml-0 pl-0" style="height: auto">
          {{ label }}
        </v-subheader>
        <v-slider
          :disabled="disabled"
          :value="getSliderValue()"
          :max="max"
          :min="min"
          hide-details
          :step="accuracy || step"
          class="niko-slider align-center ma-0 pa-0"
          @input="emitValue($event, true)"
        >
          <template slot="prepend" class="ma-0 pa-0">
            <v-row
              class="prepend-row ma-0 pa-0"
              align="center"
              justify="center"
            >
              <v-col class="ma-0 pa-0">
                <v-text-field
                  v-if="allowCommas"
                  :type="textFieldType"
                  :value="textFieldValue"
                  @change="emitValue($event)"
                  class="text-field"
                  rounded
                  outlined
                  hide-details
                  filled
                  :suffix="suffix"
                  :placeholder="placeholder"
                  oninput="if (value && value.length > maxLength) value = value.slice(0, maxLength);
                           if (value) value = value.replace('-', '').replace(/[^0-9.,]+/, '');"
                  maxlength="5"
                ></v-text-field>
                <v-text-field
                  v-else
                  :type="textFieldType"
                  :value="textFieldValue"
                  @change="emitValue($event)"
                  class="text-field"
                  rounded
                  outlined
                  hide-details
                  filled
                  :suffix="suffix"
                  :placeholder="placeholder"
                  oninput="if (value && value.length > maxLength) value = value.slice(0, maxLength);
                           if (value) value = value.replace('-', '');"
                  maxlength="5"
                ></v-text-field>
              </v-col>
              <v-col class="ml-6 pa-0" justify="center">
                <v-btn class="pa-0" icon @click="decrement">
                  <v-icon size="20px"> $iconMinus </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <template slot="append">
            <v-btn class="pa-0 ma-0 align-center" icon @click="increment">
              <v-icon class="align-center" size="20px"> $iconAdd </v-icon>
            </v-btn>
          </template>
        </v-slider>
        <v-subheader
          v-if="subLabel"
          class="sub-label text-grey-darker mb-1 ml-0 pl-0"
        >
          {{ subLabel }}
        </v-subheader>
      </v-col>
    </v-row>
  </v-responsive>
</template>
<script>
export default {
  name: "niko-slider",
  props: {
    max: Number,
    min: Number,
    label: String,
    subLabel: String,
    suffix: String,
    step: {
      type: [Number, String],
      default: 0.1,
    },
    accuracy: Number,
    value: Number,
    disabled: Boolean,
    daylightZones: Boolean,
    placeholder: String,
    textFieldType: {
      type: String,
      default: "number",
    },
  },
  computed: {
    textFieldValue() {
      if (this.value === 0 && this.daylightZones) {
        return this.$i18n.t("room.functional.daylight.zones.none");
      }
      return this.value;
    },
    allowCommas() {
      return this.accuracy && this.textFieldType === "text";
    },
  },
  methods: {
    getSliderValue() {
      if (this.value > this.max) {
        return this.max;
      }
      if (this.placeholder && this.value === null) {
        return -1;
      }
      return this.value;
    },
    decrement() {
      let newValue = this.value === null ? 0 : this.value;
      newValue -= +this.step;
      if (newValue < this.min) {
        return;
      }
      this.$emit("onChange", this.getFormattedEmittedArgs(newValue, false));
    },
    increment() {
      let newValue = this.value === null ? -Number(this.step) : this.value;
      newValue += +this.step;
      if (newValue > this.max) {
        return;
      }
      this.$emit("onChange", this.getFormattedEmittedArgs(newValue, true));
    },
    emitValue(value, preventRerender) {
      let fieldValue = value.toString().replace(",", ".");
      if (!this.placeholder && !value) {
        fieldValue = 0;
      }
      if (fieldValue === "-1") {
        fieldValue = "";
      }
      if (
        this.accuracy === 0.1 &&
        fieldValue &&
        fieldValue.split(".")[1]?.length > 1
      ) {
        fieldValue = fieldValue.slice(0, -1);
      }
      if (
        this.accuracy === 0.01 &&
        fieldValue &&
        fieldValue.split(".")[1]?.length > 2
      ) {
        fieldValue = fieldValue.slice(0, -1);
      }
      this.$emit("onChange", this.formatValue(fieldValue, preventRerender));
    },
    formatValue(fieldValue, preventRerender) {
      const formattedValue =
        fieldValue.length !== 0 ? Number(fieldValue) : null;

      if (this.allowCommas) {
        return {
          value: formattedValue,
          preventRerender: preventRerender,
        };
      }

      return formattedValue;
    },
    getFormattedEmittedArgs(value, isIncrement) {
      const formattedDecrementValue = value === -1 ? null : value;
      if (this.allowCommas) {
        return {
          value: isIncrement ? value : formattedDecrementValue,
          preventRerender: true,
        };
      }

      return isIncrement ? value : formattedDecrementValue;
    },
  },
};
</script>
<style lang="scss">
.niko-slider {
  max-width: 440px;
  max-height: 50px;
  .prepend-row {
    flex-wrap: inherit;
  }
  .v-slider__thumb {
    width: 32px;
    height: 32px;
  }
  .v-input__slot {
    max-width: 200px;
    margin-bottom: 0px;
  }
  .v-slider--horizontal .v-slider__track-container {
    height: 4px;
  }
  .text-field {
    width: 100px;
  }
  .v-slider__thumb:before {
    content: "";
    color: inherit;
    border-radius: 50%;
    opacity: 0;
    position: absolute;
    left: -12px;
    top: -12px;
    transform: scale(0.1);
    pointer-events: none;
  }
  .v-text-field--rounded > .v-input__control > .v-input__slot {
    padding: 0 16px;
  }
  .v-slider--horizontal .v-slider__track-background,
  .v-slider--horizontal .v-slider__track-fill {
    height: 100%;
    border-radius: 2px;
  }
  .v-input--hide-details > .v-input__control > .v-input__slot {
    margin-bottom: 0;
    margin-left: 6px;
  }

  .text-field input[type="number"],
  .text-field input[type="text"] {
    -moz-appearance: textfield;
  }
  .text-field input::-webkit-outer-spin-button,
  .text-field input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}
.sub-label {
  height: auto;
}
</style>
